import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiBox,
  FiCheckCircle,
  FiClipboard,
  FiDollarSign,
  FiFileText,
  FiLoader,
  FiPackage,
  FiPrinter,
  FiRotateCcw,
  FiTruck,
  FiUser,
  FiX,
  FiXCircle,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

export default function SalesReturnDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { accessToken } = useAuth();

  const [salesReturn, setSalesReturn] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReceiving, setIsReceiving] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [error, setError] = useState("");

  const loadReturn = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest(`/sales-returns/${id}`, {
        accessToken,
      });

      setSalesReturn(response);
    } catch (requestError) {
      setError(requestError.message || "Could not load sales return.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    loadReturn();
  }, [loadReturn]);

  const items = Array.isArray(salesReturn?.items) ? salesReturn.items : [];

  const totals = useMemo(() => {
    const creditAmount =
      salesReturn?.totalCreditAmount !== undefined &&
      salesReturn?.totalCreditAmount !== null
        ? Number(salesReturn.totalCreditAmount)
        : items.reduce(
            (total, item) =>
              total +
              Number(
                item.creditAmount ??
                  item.lineCreditAmount ??
                  item.totalAmount ??
                  0,
              ),
            0,
          );

    return {
      itemCount: items.length,
      quantity: items.reduce(
        (total, item) => total + Number(item.quantityReturned || 0),
        0,
      ),
      restockCount: items.filter((item) => item.restockToInventory).length,
      creditAmount,
    };
  }, [salesReturn, items]);

  const invoice = salesReturn?.salesInvoice || salesReturn?.invoice;
  const creditNote =
    salesReturn?.creditNote || salesReturn?.salesCreditNote || null;

  const receiveReturn = async () => {
    setIsReceiving(true);
    setError("");

    try {
      await apiRequest(`/sales-returns/${salesReturn.id}/receive`, {
        method: "POST",
        accessToken,
      });

      setShowReceiveModal(false);
      await loadReturn();
    } catch (requestError) {
      setError(requestError.message || "Could not receive this sales return.");
    } finally {
      setIsReceiving(false);
    }
  };

  const cancelReturn = async () => {
    if (!cancelReason.trim()) {
      setError("Please enter a cancellation reason.");
      return;
    }

    setIsCancelling(true);
    setError("");

    try {
      await apiRequest(`/sales-returns/${salesReturn.id}/cancel`, {
        method: "POST",
        accessToken,
        body: {
          reason: cancelReason.trim(),
        },
      });

      setShowCancelModal(false);
      await loadReturn();
    } catch (requestError) {
      setError(requestError.message || "Could not cancel this sales return.");
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[420px] flex items-center justify-center text-primary-700">
        <FiLoader className="animate-spin mr-3" size={24} />
        Loading sales return...
      </div>
    );
  }

  if (!salesReturn) {
    return (
      <div className="bg-white border border-red-100 rounded-3xl p-8">
        <p className="text-red-700">{error || "Sales return not found."}</p>

        <button
          onClick={() => navigate("/admindashboard/sales/returns")}
          className="mt-5 h-11 px-5 rounded-xl bg-primary-700 text-white"
        >
          Back to Sales Returns
        </button>
      </div>
    );
  }

  const status = toTitle(salesReturn.status);
  const isDraft = salesReturn.status === "DRAFT";
  const isReceived = salesReturn.status === "RECEIVED";
  const isCancelled = salesReturn.status === "CANCELLED";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <button
            onClick={() => navigate("/admindashboard/sales/returns")}
            className="flex items-center gap-2 text-gray-500 hover:text-primary-700 mb-4"
          >
            <FiArrowLeft />
            Back to Sales Returns
          </button>

          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-4xl font-bold text-gray-900">
              {salesReturn.returnCode}
            </h1>

            <ReturnStatusBadge status={status} />
          </div>

          <p className="text-gray-500 mt-3">
            Sales Return Details · {salesReturn.store?.name || "—"}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => window.print()}
            className="h-11 px-5 rounded-xl border border-gray-200 bg-white flex items-center gap-2 hover:bg-gray-50"
          >
            <FiPrinter />
            Print
          </button>

          {isDraft && (
            <>
              <button
                onClick={() => {
                  setCancelReason("");
                  setShowCancelModal(true);
                }}
                className="h-11 px-5 rounded-xl border border-red-200 text-red-600 hover:bg-red-50"
              >
                Cancel Draft
              </button>

              <button
                onClick={() => setShowReceiveModal(true)}
                className="h-11 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2 hover:bg-primary-800"
              >
                <FiTruck />
                Receive Return
              </button>
            </>
          )}

          {isReceived && creditNote && (
            <button
              onClick={() =>
                navigate(
                  `/admindashboard/sales/credit-notes/${creditNote.id}`,
                )
              }
              className="h-11 px-5 rounded-xl bg-primary-700 text-white flex items-center gap-2 hover:bg-primary-800"
            >
              <FiFileText />
              View Credit Note
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700 flex gap-3">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {isCancelled && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700 flex gap-3">
          <FiXCircle className="mt-0.5 shrink-0" size={20} />
          <div>
            <p className="font-semibold">This sales return was cancelled.</p>
            <p className="text-sm mt-1">
              {salesReturn.cancellationReason ||
                salesReturn.cancelReason ||
                "No cancellation reason was recorded."}
            </p>
          </div>
        </div>
      )}

      {isReceived && (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-5 text-green-800 flex gap-3">
          <FiCheckCircle className="mt-0.5 shrink-0" size={20} />
          <div>
            <p className="font-semibold">
              Return received and credit note issued.
            </p>
            <p className="text-sm mt-1">
              {creditNote?.creditNoteCode
                ? `${creditNote.creditNoteCode} was issued against this return.`
                : "The invoice receivable has been reduced by the return credit value."}
            </p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        <MiniCard
          title="Credit Value"
          value={formatMoney(totals.creditAmount)}
          icon={<FiDollarSign size={23} />}
          color="bg-primary-50 text-primary-700"
        />

        <MiniCard
          title="Return Lines"
          value={totals.itemCount}
          icon={<FiClipboard size={23} />}
          color="bg-blue-50 text-blue-700"
        />

        <MiniCard
          title="Total Quantity"
          value={totals.quantity}
          icon={<FiPackage size={23} />}
          color="bg-purple-50 text-purple-700"
        />

        <MiniCard
          title="Restock Lines"
          value={totals.restockCount}
          icon={<FiBox size={23} />}
          color="bg-green-50 text-green-700"
        />
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <InfoPanel
          title="Customer Information"
          icon={<FiUser size={23} />}
          iconClassName="bg-primary-50 text-primary-700"
          rows={[
            [
              "Customer",
              invoice?.customerNameSnapshot ||
                salesReturn.customer?.fullName ||
                "Walk-in Customer",
            ],
            ["Phone", invoice?.customerPhoneSnapshot],
            ["Email", invoice?.customerEmailSnapshot],
            ["Store", salesReturn.store?.name],
          ]}
        />

        <InfoPanel
          title="Return Information"
          icon={<FiRotateCcw size={23} />}
          iconClassName="bg-orange-50 text-orange-700"
          rows={[
            ["Return Number", salesReturn.returnCode],
            ["Return Date", formatDate(salesReturn.createdAt)],
            ["Invoice", invoice?.invoiceCode],
            ["Reason", salesReturn.reason],
            ["Received At", salesReturn.receivedAt ? formatDateTime(salesReturn.receivedAt) : "Not received yet"],
          ]}
        />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        <div className="p-7 border-b border-gray-100">
          <h2 className="text-2xl font-bold">Returned Items</h2>

          <p className="text-gray-500 mt-1">
            Stock is restored only for lines marked as restockable when this
            return is received.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px]">
            <thead className="bg-gray-50">
              <tr>
                <TableHeader>Product</TableHeader>
                <TableHeader>SKU</TableHeader>
                <TableHeader center>Returned Qty</TableHeader>
                <TableHeader center>Restock</TableHeader>
                <TableHeader right>Unit Credit</TableHeader>
                <TableHeader right>Credit Amount</TableHeader>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => {
                const invoiceItem =
                  item.salesInvoiceItem || item.invoiceItem || null;

                const quantity = Number(item.quantityReturned || 0);
                const creditAmount = Number(
                  item.creditAmount ??
                    item.lineCreditAmount ??
                    item.totalAmount ??
                    0,
                );

                return (
                  <tr
                    key={item.id}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <td className="px-5 py-5">
                      <h3 className="font-semibold">
                        {item.productNameSnapshot ||
                          invoiceItem?.productNameSnapshot ||
                          "Product"}
                      </h3>

                      <p className="text-sm text-gray-500 mt-1">
                        {item.variantNameSnapshot ||
                          invoiceItem?.variantNameSnapshot ||
                          "Default variant"}
                      </p>
                    </td>

                    <td className="px-5 py-5 text-gray-600">
                      {item.skuSnapshot || invoiceItem?.skuSnapshot || "—"}
                    </td>

                    <td className="px-5 py-5 text-center font-semibold">
                      {quantity}
                    </td>

                    <td className="px-5 py-5 text-center">
                      {item.restockToInventory ? (
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                          <FiCheckCircle />
                          Restock
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                          <FiX />
                          Do not restock
                        </span>
                      )}
                    </td>

                    <td className="px-5 py-5 text-right">
                      {formatMoney(quantity > 0 ? creditAmount / quantity : 0)}
                    </td>

                    <td className="px-5 py-5 text-right font-semibold text-primary-700">
                      {formatMoney(creditAmount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white rounded-3xl border border-gray-100 p-7">
          <h2 className="text-2xl font-bold">Internal Notes</h2>

          <p className="mt-4 text-gray-600 whitespace-pre-wrap">
            {salesReturn.notes || "No notes were added to this return."}
          </p>
        </div>

        <div className="bg-gray-50 rounded-3xl border border-gray-100 p-7">
          <h2 className="text-xl font-bold">Credit Note</h2>

          {creditNote ? (
            <div className="mt-5">
              <p className="text-sm text-gray-500">Credit Note Number</p>
              <p className="mt-1 font-bold text-primary-700">
                {creditNote.creditNoteCode}
              </p>

              <p className="text-sm text-gray-500 mt-5">Credit Amount</p>
              <p className="mt-1 text-2xl font-bold">
                {formatMoney(
                  creditNote.totalAmount ??
                    creditNote.creditAmount ??
                    totals.creditAmount,
                )}
              </p>

              <button
                onClick={() =>
                  navigate(
                    `/admindashboard/sales/credit-notes/${creditNote.id}`,
                  )
                }
                className="mt-6 h-10 px-4 rounded-xl bg-primary-700 text-white text-sm font-medium"
              >
                Open Credit Note
              </button>
            </div>
          ) : (
            <p className="mt-5 text-gray-500 text-sm">
              A credit note will be generated automatically after the return is
              received.
            </p>
          )}
        </div>
      </div>

      <ReceiveReturnModal
        open={showReceiveModal}
        onClose={() => setShowReceiveModal(false)}
        onConfirm={receiveReturn}
        isReceiving={isReceiving}
        returnCode={salesReturn.returnCode}
        creditAmount={totals.creditAmount}
        restockCount={totals.restockCount}
      />

      <CancelReturnModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        reason={cancelReason}
        setReason={setCancelReason}
        onConfirm={cancelReturn}
        isCancelling={isCancelling}
      />
    </div>
  );
}

function ReceiveReturnModal({
  open,
  onClose,
  onConfirm,
  isReceiving,
  returnCode,
  creditAmount,
  restockCount,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="px-7 py-6 border-b">
          <h2 className="text-2xl font-bold">Receive Sales Return</h2>
          <p className="text-sm text-gray-500 mt-1">
            Confirm that the physical items have been received.
          </p>
        </div>

        <div className="p-7">
          <div className="rounded-2xl bg-primary-50 p-5 text-primary-900">
            <p className="font-semibold">{returnCode}</p>

            <p className="text-sm mt-3">
              This action will restore inventory for {restockCount} selected
              line{restockCount === 1 ? "" : "s"} and issue a credit note for{" "}
              <strong>{formatMoney(creditAmount)}</strong>.
            </p>
          </div>

          <p className="mt-5 text-sm text-gray-500">
            This action cannot be undone through this page.
          </p>
        </div>

        <div className="px-7 py-5 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isReceiving}
            className="h-11 px-5 rounded-xl border border-gray-200"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={isReceiving}
            className="h-11 px-5 rounded-xl bg-primary-700 text-white flex items-center gap-2 disabled:opacity-60"
          >
            {isReceiving && <FiLoader className="animate-spin" />}
            Confirm Receipt
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelReturnModal({
  open,
  onClose,
  reason,
  setReason,
  onConfirm,
  isCancelling,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="px-7 py-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold">Cancel Sales Return</h2>
            <p className="text-sm text-gray-500 mt-1">
              Only draft returns can be cancelled.
            </p>
          </div>

          <button
            onClick={onClose}
            className="h-10 w-10 rounded-xl bg-gray-100 flex items-center justify-center"
          >
            <FiX />
          </button>
        </div>

        <div className="p-7">
          <label className="block text-sm font-medium mb-2">
            Cancellation Reason *
          </label>

          <textarea
            rows={4}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Explain why this return draft is being cancelled..."
            className="w-full rounded-2xl border border-gray-200 p-4 resize-none focus:outline-none focus:border-primary-700"
          />
        </div>

        <div className="px-7 py-5 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isCancelling}
            className="h-11 px-5 rounded-xl border border-gray-200"
          >
            Keep Draft
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={isCancelling}
            className="h-11 px-5 rounded-xl bg-red-600 text-white flex items-center gap-2 disabled:opacity-60"
          >
            {isCancelling && <FiLoader className="animate-spin" />}
            Cancel Return
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoPanel({ title, icon, iconClassName, rows }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-7">
      <div className="flex items-center gap-3 mb-6">
        <div
          className={`h-11 w-11 rounded-2xl flex items-center justify-center ${iconClassName}`}
        >
          {icon}
        </div>

        <h2 className="text-xl font-bold">{title}</h2>
      </div>

      <div>
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex justify-between gap-5 py-4 border-b border-gray-100 last:border-b-0"
          >
            <span className="text-gray-500">{label}</span>
            <span className="font-semibold text-right">{value || "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniCard({ title, value, icon, color }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold mt-3">{value}</p>
        </div>

        <div
          className={`h-14 w-14 rounded-2xl flex items-center justify-center ${color}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function TableHeader({ children, right = false, center = false }) {
  return (
    <th
      className={`px-5 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 ${
        right ? "text-right" : center ? "text-center" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function ReturnStatusBadge({ status }) {
  const colors = {
    Draft: "bg-yellow-100 text-yellow-700",
    Received: "bg-green-100 text-green-700",
    Cancelled: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        colors[status] || "bg-gray-100 text-gray-700"
      }`}
    >
      {status}
    </span>
  );
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "—";

  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function toTitle(value = "") {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}